'use client'

import ScreenLoader from '@/app/[lng]/components/Skeletons/ScreenLoader'
import CustomErrorBoundary from '@/app/[lng]/ErrorBoundary'
import { useGetVarietiesListQuery } from '@/app/globalRedux/api/constantSlice'
import {
  useAddProductServicesMutation,
  useGetProductServicesQuery,
  useUpdateProductServicesMutation,
} from '@/app/globalRedux/api/productServiceSlice'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import {
  EditProductModalProps,
  ProductResponse,
  ProductServices,
  Variety,
} from '@/app/types/productServiceType'
import { monthNameToNumber } from '@/app/utils/numberToMonth'
import {
  productNoOptionText,
  productOptions,
} from '@/app/utils/productFormatData'
import AvailablityMultiSelect from '@/components/AvailablityMultiSelect'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  MultiSelector,
  MultiSelectorContent,
  MultiSelectorInput,
  MultiSelectorItem,
  MultiSelectorList,
  MultiSelectorTrigger,
} from '@/components/ui/multiSelect'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import CreatableMultiSelect from '@/components/ui/select/CreatableMultiSelect'
import MultiSelect from '@/components/ui/select/MutiSelect'
import {
  colourStyles,
  formatReactSelectOption,
  generateSingleValueLabelFormate,
} from '@/components/ui/select/selectStyle'
import { fetchUrlAndConvertToFiles } from '@/services/generalDataServices'
import { getSingleProduct } from '@/services/productService'
import Image from 'next/image'
import { ChangeEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import SingleSelect from 'react-select'
import { toast } from 'sonner'

const initialState: ProductServices = {
  id: 0,
  product: {
    id: -1,
    name: '',
  },
  product_type: {
    id: '',
  },
  variety: {
    varietySearchText: '',
    selectedVariety: [],
  },
  tonnage: '',
  origin: {
    country: [],
  },
  tonnageUnit: '',

  available_months: { months: [] },
  image: '',
  countryId: 0,
  imageBlob: null,
  countryName: '',
  unit: { id: 0, name: '' },
  varieties: [],
  priority: '',
  is_sponsored: false,
  certifications: [],
}
const AddOrEditProductsForm = ({
  functionality,
  setOpen,
  id,
}: EditProductModalProps) => {
  const { t } = useTranslation()

  const userSubscription = useSelector(
    (state: RootState) => state.user.userSubscription
  )
  const { data: servicesData } = useGetProductServicesQuery<{
    data: ProductResponse
  }>({})
  const {
    unitsList,
    productTypesList,
    productCertificationsList,
    countriesList,
  } = useSelector((state: RootState) => {
    return state.constantData
  })
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false)
  const [isBtnClicked, setIsBtnClicked] = useState<boolean>(false)
  const [sponsoredProduct, setSponsoredProduct] = useState<boolean>(false)
  const [currentMonth, setCurrentMonth] = useState<string[]>([])
  const [image, setImage] = useState<string | null>(null)
  const [originList, setOriginList] = useState<string[]>([])
  const [productSearchText, setProductSearchText] = useState<string>('')
  const [isGettingData, setIsGettingData] = useState<boolean>(false)
  const [productList, setProductList] = useState<any>([])
  const [product, setProduct] = useState<ProductServices>(initialState)
  const [isProductLoading, setIsProductLoading] = useState(false)
  const [addProductServices] = useAddProductServicesMutation()
  const [updateProductServices] = useUpdateProductServicesMutation()
  const { data: varietyOptionList = [], isFetching: isVarietyFetching } =
    useGetVarietiesListQuery(
      {
        varietySearchText: product?.variety?.varietySearchText,
        productId: product?.product?.id,
      },
      {
        skip: product?.product.id == -1,
      }
    )

  const getIndividualProduct = async () => {
    setIsProductLoading(true)
    const responseData = await getSingleProduct(id || -1)
    if (responseData?.image) {
      const img = await fetchUrlAndConvertToFiles(responseData?.image)
      if (img) {
        setProduct((prevProduct) => ({ ...prevProduct, imageBlob: img[0] }))
      }
    }
    setImage(responseData?.image)
    setSponsoredProduct(responseData?.is_sponsored ?? false)
    setProductSearchText(responseData?.product?.name)
    setCurrentMonth(responseData?.available_months?.months ?? [])

    setOriginList(responseData?.origin?.map((item: any) => item.name))
    setProduct((prevProduct) => ({
      ...prevProduct,
      variety: {
        ...prevProduct.variety,
        selectedVariety: responseData?.varieties?.map((item: Variety) =>
          formatReactSelectOption(item)
        ),
      },
      tonnageUnit: String(responseData?.unit?.id ?? ''),
      tonnage: responseData?.tonnage ?? '',
      product_type: {
        id: responseData?.product_type?.id ?? '',
      },
      is_sponsored: responseData?.is_sponsored ?? false,
      priority: responseData?.priority ?? '',
      product: {
        id: responseData.product?.id,
        name: responseData.product?.name,
      },
      certifications: responseData?.certifications?.map(
        (item: { id: number; name: string }) => formatReactSelectOption(item)
      ),
    }))
    setIsProductLoading(false)
  }

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const reader = new FileReader()

    if (file) {
      if (file.type?.startsWith('image/')) {
        if (file.size <= 3 * 1000 * 1000) {
          reader.onload = () => {
            if (typeof reader.result === 'string') {
              setImage(reader.result)
              updateProductImage(file)
            }
          }
          reader.readAsDataURL(file)
        } else {
          toast.warning(t('File size exceeds the limit of 3MB'))
          return
        }
      } else {
        toast.warning(t('Invalid file type.'))
      }
    }
  }

  const handleButtonClick = (): void => {
    const fileInput = document.getElementById('product_img') as HTMLInputElement
    if (fileInput) {
      fileInput.click()
    }
  }

  const updateProductImage = (imageData: string | File) => {
    setProduct({ ...product, imageBlob: imageData })
  }

  const updateMonth = (value: string[]) => {
    setCurrentMonth(value)
  }

  const createFormData = async () => {
    const formData = new FormData()

    if (product?.product_type?.id) {
      formData.append(
        'company_product[product_type_id]',
        String(product.product_type.id)
      )
    }

    if (product?.variety?.selectedVariety?.length > 0) {
      product?.variety?.selectedVariety
        .filter((item) => item.id !== -1 && item.id)
        .forEach((item) =>
          formData.append('company_product[variety_ids][]', String(item?.id))
        )

      product?.variety?.selectedVariety
        ?.filter((item) => item.id == -1 || item.__isNew__)
        ?.forEach((item) => {
          formData.append(
            'company_product[company_product_varieties_attributes][][custom_variety]',
            item?.label
          )
        })
    }
    if (product?.product.id > 0) {
      formData.append(
        'company_product[product_id]',
        String(product?.product?.id)
      )
    }
    if (originList?.length > 0) {
      countriesList.forEach((item: any) => {
        if (originList?.includes(item?.name)) {
          formData.append('company_product[origin_ids][]', String(item.id))
        }
      })
    }
    if (product?.imageBlob) {
      if (image && image?.startsWith('https://')) {
        formData.append('company_product[clone]', String(true))
        formData.append('company_product[image_id]', String(id))
      } else {
        formData.append('company_product[image]', product?.imageBlob)
      }
    }
    if (product?.tonnage) {
      formData.append('company_product[tonnage]', product.tonnage)
    }
    if (product?.tonnageUnit && Number(product?.tonnageUnit) > 0) {
      formData.append('company_product[unit_id]', String(product.tonnageUnit))
    }
    if (currentMonth?.length) {
      currentMonth.forEach((e) => {
        if (e) {
          formData.append(
            `company_product[availabilities_attributes][][month]`,
            String(monthNameToNumber[e])
          )
        }
      })
    }
    if (product?.priority) {
      formData.append('company_product[priority]', product.priority)
    }
    formData.append(
      'company_product[is_sponsored_product]',
      String(product.is_sponsored)
    )
    if (product?.certifications?.length > 0) {
      const ids: string[] = product?.certifications.map((item: any) => item.id)
      ids.forEach((id) => {
        formData.append('company_product[certification_ids][]', id)
      })
    }

    return formData
  }
  const submit = async () => {
    if (
      product?.product?.id <= 0 ||
      originList?.length == 0 ||
      currentMonth?.length <= 0 ||
      Number(product?.priority) <= 0
    ) {
      setIsFormSubmitted(true)
      setIsBtnClicked(false)
      toast.warning(t('Please fill in all required fields'))
      return
    }

    const formData = await createFormData()
    if (['Add', 'Clone'].includes(functionality)) {
      try {
        const response = await addProductServices(formData).unwrap()
        if (response.status === 'success') {
          setOpen(false)
          setProduct(initialState)
          setIsBtnClicked(false)
        }
      } catch (error) {
        setIsBtnClicked(false)
      }
    } else if (functionality == 'Edit') {
      try {
        const response: { status: string } = await updateProductServices({
          id: id || -1,
          products: formData,
        }).unwrap()
        if (response.status == 'success') {
          setOpen(false)
          setProduct(initialState)
          setIsBtnClicked(false)
        }
      } catch (error) {
        setIsBtnClicked(false)
      }
    }
  }

  const getProducts = async () => {
    setProductList(await productOptions(productSearchText))
    setIsGettingData(false)
  }

  useEffect(() => {
    if (['Edit', 'Clone'].includes(functionality)) {
      getIndividualProduct()
    }
  }, [])

  useEffect(() => {
    if (productSearchText) {
      setIsGettingData(true)
      getProducts()
    }
  }, [productSearchText])
  useEffect(() => {
    if (
      sponsoredProduct &&
      functionality === 'Clone' &&
      servicesData &&
      servicesData?.sponsored_product_count ===
        servicesData?.sponsored_products_total_count
    ) {
      setProduct({ ...product, is_sponsored: false })
    }
  }, [sponsoredProduct])
  return (
    <>
      <div className=" flex flex-col gap-4 ">
        <div className="grid desktop:grid-cols-2 justify-between gap-5 mx-8 mt-5">
          <div className="grid w-full items-center gap-3">
            <Label htmlFor="product_img">{t('PRODUCT IMAGE')}</Label>

            <div className="flex items-center gap-6 mobile:flex-col mobile:items-start">
              <Image
                loading="lazy"
                src={image ? image : '/assets/Icons/noImage/noImage2.svg'}
                alt="Preview"
                height={100}
                className="object-cover w-[70px] h-[70px] rounded"
                width={100}
              />
              <div className="flex flex-col gap-2">
                <div>
                  <Button
                    variant="secondary"
                    onClick={handleButtonClick}
                    className="text-light_black font-ns_bold bg-pale_gold !border-pale_gold"
                    size="icon"
                  >
                    <Image
                      loading="lazy"
                      width={16}
                      height={15}
                      src="/assets/Icons/uploadIcon.svg"
                      alt="upload-icon"
                    />
                    {t('Upload Photo')}
                  </Button>
                </div>
                <p className="text-[10px] text-gray_text">
                  {t('At least 130 x 130px PNG, JPG, JPEG, WEBP file')}
                </p>
              </div>
              <input
                id="product_img"
                type="file"
                className="hidden"
                accept="image/png, image/jpeg, image/webp, image/jpg"
                onChange={handleImageChange}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col  justify-center gap-8 mx-8 my-4">
          <div className="flex gap-10 mobile:flex-col">
            <div
              className={`flex flex-col w-full gap-2 relative ${functionality == 'Edit' && sponsoredProduct ? 'cursor-not-allowed' : ''}`}
            >
              <Label
                htmlFor="product_name"
                className={`required ${functionality == 'Edit' && sponsoredProduct ? 'text-placeholder cursor-not-allowed' : ''}`}
              >
                {t('PRODUCT NAME')}
              </Label>
              <SingleSelect
                options={productList}
                onChange={(option: any) =>
                  setProduct({
                    ...product,
                    product: {
                      id: option.value,
                      name: option.label,
                    },
                    variety: {
                      varietySearchText: '',
                      selectedVariety: [],
                    },
                  })
                }
                onBlur={() => {
                  setProductList([])
                }}
                isDisabled={functionality == 'Edit' && sponsoredProduct}
                noOptionsMessage={({ inputValue }) =>
                  t(productNoOptionText(inputValue, isGettingData))
                }
                onInputChange={(value: string) => {
                  setProductSearchText(value)
                }}
                value={
                  product?.product?.id !== -1
                    ? generateSingleValueLabelFormate(product?.product)
                    : []
                }
                placeholder={t('Select Product')}
                styles={colourStyles}
                hideSelectedOptions
                menuPlacement="auto"
              />
              {isFormSubmitted && !product?.product?.name && (
                <div className="text-required_color text-xs -bottom-5 absolute ">
                  {t('Please Select Product')}
                </div>
              )}
            </div>

            <div className=" flex flex-col gap-2 w-full relative">
              <Label htmlFor="origin" className="required">
                {t('ORIGIN')}
              </Label>
              <MultiSelector
                values={originList ?? []}
                onValuesChange={setOriginList}
              >
                <MultiSelectorTrigger>
                  <MultiSelectorInput
                    placeholder={
                      originList?.length <= 0 ? t('Select The Origin') : ''
                    }
                  />
                </MultiSelectorTrigger>
                <MultiSelectorContent>
                  <MultiSelectorList>
                    {countriesList?.map((country: any) => (
                      <MultiSelectorItem
                        key={country?.name}
                        value={country?.name}
                      >
                        {country?.name}
                      </MultiSelectorItem>
                    ))}
                  </MultiSelectorList>
                </MultiSelectorContent>
              </MultiSelector>

              {isFormSubmitted && originList?.length == 0 && (
                <div className="text-required_color text-xs -bottom-5 absolute ">
                  {t('Please Select Origin')}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row gap-10 w-full mobile:flex-col">
            <div className="flex flex-col gap-2 w-full">
              <Label htmlFor="variety">{t('Variety')}</Label>
              <CreatableMultiSelect
                id="variety"
                inputValue={product?.variety?.varietySearchText}
                value={product?.variety?.selectedVariety}
                setValue={(value) => {
                  setProduct((prev) => ({
                    ...prev,
                    variety: { ...prev?.variety, selectedVariety: value },
                  }))
                }}
                setInputValue={(value) => {
                  setProduct((prev) => ({
                    ...prev,
                    variety: { ...prev?.variety, varietySearchText: value },
                  }))
                }}
                options={varietyOptionList?.varieties?.map((item: any) => ({
                  id: item?.id,
                  label: item?.name,
                  value: item?.name,
                }))}
                placeholder={t(
                  product?.product?.id == -1
                    ? t('Choose Product First')
                    : t('Select Variety')
                )}
                createLabel={t('Create New Variety')}
                isDisabled={product?.product?.id == -1}
                isLoading={isVarietyFetching}
              />
            </div>
            <div className="flex flex-col gap-2 w-full ">
              <Label htmlFor="product_type">{t('PRODUCT TYPE')}</Label>
              <Select
                value={
                  product?.product_type?.id
                    ? String(product?.product_type?.id)
                    : ''
                }
                onValueChange={(event) => {
                  setProduct({
                    ...product,
                    product_type: {
                      id: event,
                    },
                  })
                }}
              >
                <SelectTrigger id="product_type">
                  <SelectValue
                    placeholder={t('Select Product Type')}
                    className="select text-[#707070]"
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {productTypesList.map((product: any) => (
                      <SelectItem value={String(product.id)} key={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-row gap-10 w-full mobile:flex-col">
            <div className=" flex flex-row gap-7 w-full mobile:flex-col">
              <div className=" flex flex-col gap-2 w-full">
                <Label htmlFor="quantity">{t('Quantity')}</Label>
                <Input
                  type="text"
                  id="quantity"
                  value={product.tonnage}
                  placeholder={t('Enter Quantity')}
                  onChange={(e) => {
                    const tonnageValue = e.target.value.replace(/[^0-9]/g, '')
                    setProduct({ ...product, tonnage: tonnageValue })
                  }}
                />
              </div>
              <div className=" flex flex-col gap-2 w-full">
                <Label htmlFor="unit">{t('Unit')}</Label>
                <Select
                  value={product?.tonnageUnit ? product.tonnageUnit : ''}
                  onValueChange={(event) => {
                    setProduct({
                      ...product,
                      tonnageUnit: event,
                    })
                  }}
                >
                  <SelectTrigger id="unit">
                    <SelectValue placeholder={t('Select Quantity Unit')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {unitsList &&
                        Array.isArray(unitsList) &&
                        unitsList.map((item: any, index: number) => (
                          <CustomErrorBoundary key={index}>
                            <SelectItem key={index} value={item.id.toString()}>
                              {item.name}
                            </SelectItem>
                          </CustomErrorBoundary>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className=" flex flex-col gap-2 w-full relative">
              <Label htmlFor="availability" className="required">
                {t('AVAILABILITY')}
              </Label>
              <AvailablityMultiSelect
                selectedMonth={currentMonth}
                setValue={updateMonth}
              />
              {isFormSubmitted && currentMonth.length <= 0 && (
                <div className="text-required_color text-xs -bottom-5 absolute ">
                  {t('Please Select Availability')}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-row gap-10 w-full mobile:flex-col">
            <div className="flex flex-col gap-2 w-full">
              <Label htmlFor="certificates">{t('Product Certification')}</Label>
              <MultiSelect
                id="certificates"
                placeholder={
                  product.certifications.length <= 0
                    ? t('Select Certification')
                    : ''
                }
                value={product.certifications}
                options={productCertificationsList?.map((item: any) => ({
                  id: item.id,
                  label: item.name,
                  value: item.name,
                }))}
                setValue={(value) => {
                  if (value) {
                    setProduct((prevProduct) => ({
                      ...prevProduct,
                      certifications: value,
                    }))
                  }
                }}
                isLoading={false}
                isDisabled={false}
                noOptionsMessage="No Certifications Available"
                hideSelectedOptions={true}
                selectAllOptionEnabled={true}
              />
            </div>
            <div className="flex flex-col gap-2 w-full relative">
              <Label htmlFor="priority" className="required">
                {t('Priority Number')}
              </Label>
              <Input
                id="priority"
                value={product?.priority ? product?.priority : ''}
                onChange={(event) => {
                  setProduct({
                    ...product,
                    priority: event.target.value.replace(/[^0-9]/g, ''),
                  })
                }}
                type="string"
                placeholder={t('Enter Priority Number')}
              />
              {isFormSubmitted && !product?.priority && (
                <div className="text-required_color text-xs -bottom-5 absolute ">
                  {t('Please Enter Valid Priority Number')}
                </div>
              )}
            </div>
          </div>
          {userSubscription &&
            (userSubscription === 'Gold' ||
              userSubscription === 'Platinum') && (
              <div className="relative mb-4 -mt-2">
                <div className="absolute">
                  <Label
                    htmlFor="is_sponsored"
                    className={`text-sm flex items-center gap-2 font-ns_semibold mt-1 tooltip ${functionality == 'Edit' && sponsoredProduct ? 'text-placeholder cursor-not-allowed' : ''}`}
                  >
                    <Checkbox
                      className="h-[15px] w-[15px]"
                      id="is_sponsored"
                      name="is_sponsored"
                      checked={product?.is_sponsored}
                      onClick={() => {
                        setProduct({
                          ...product,
                          is_sponsored: !product?.is_sponsored,
                        })
                      }}
                      disabled={
                        (servicesData?.sponsored_products_total_count >= 0 &&
                          servicesData?.sponsored_product_count ===
                            servicesData?.sponsored_products_total_count) ||
                        (sponsoredProduct && functionality == 'Edit')
                      }
                    />
                    <p>{t('Sponsored Product')}</p>
                    <span className="tooltiptext">
                      {servicesData?.sponsored_product_count ===
                      servicesData?.sponsored_products_total_count
                        ? t('Maximum Limit Reached')
                        : t(
                            'You cannot change the sponsored product for a year.'
                          )}
                      {`(`}
                      {t('Limit')}{' '}
                      {`${servicesData?.sponsored_product_count}/${servicesData?.sponsored_products_total_count})`}
                    </span>
                  </Label>
                </div>
              </div>
            )}
        </div>
        <div className=" flex flex-row  justify-center gap-x-3 bg-[#f7f7f7] py-[30px]">
          <Button
            variant="secondary"
            size="sx"
            className=" uppercase"
            disabled={isBtnClicked}
            onClick={async () => {
              setIsBtnClicked(true)
              await submit()
            }}
          >
            {functionality == 'Edit' ? `${t('UPDATE')}` : `${t('ADD PRODUCT')}`}
          </Button>
          <Button
            type="submit"
            variant="grayBtn"
            size="sx"
            className=" uppercase"
            onClick={() => {
              setOpen(false)
            }}
          >
            {t('CANCEL')}
          </Button>
        </div>
      </div>
      <ScreenLoader isLoadingScreen={isProductLoading} onModal={true} />
    </>
  )
}

export default AddOrEditProductsForm
